import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  CheckCircle,
  Plus,
  Edit,
  Trash2,
  ChevronRight,
  ChevronDown,
  CalendarDays,
} from "lucide-react";
import type { AuditTask, UserStub } from "../../_types/assignment-types";

interface TasksTabProps {
  tasks: AuditTask[];
  availableUsers: UserStub[];
  expandedTasks: Set<string>;
  onAddTaskClick: () => void;
  onToggleTaskExpansion: (taskId: string) => void;
  onUpdateTaskStatus: (taskId: string, newStatus: string) => void;
  onDeleteTask: (taskId: string) => void;
  formatDate: (date: Date | string | null) => string;
  getDaysRemaining: (date: Date | string | null) => number | null;
  getStatusColor: (status: string) => string;
}

export function TasksTab({
  tasks,
  availableUsers,
  expandedTasks,
  onAddTaskClick,
  onToggleTaskExpansion,
  onUpdateTaskStatus,
  onDeleteTask,
  formatDate,
  getDaysRemaining,
  getStatusColor,
}: TasksTabProps) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5" />
              Tasks
            </CardTitle>
            <CardDescription>
              Assignment tasks and progress tracking
            </CardDescription>
          </div>
          <Button onClick={onAddTaskClick}>
            <Plus className="h-4 w-4 mr-2" />
            Add Task
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {tasks.map((task) => {
            const assignee = availableUsers.find((u) => u.id === task.assigneeId);
            const isExpanded = expandedTasks.has(task.id);
            const daysRemaining = task.dueDate
              ? getDaysRemaining(task.dueDate)
              : null;

            return (
              <div key={task.id} className="border rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onToggleTaskExpansion(task.id)}
                        className="h-6 w-6 p-0"
                      >
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </Button>
                      <p className="font-medium">{task.description}</p>
                    </div>

                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span>Assigned to: {assignee?.name || "Unassigned"}</span>
                      {task.dueDate && (
                        <span className="flex items-center gap-1">
                          <CalendarDays className="h-4 w-4" />
                          Due: {formatDate(task.dueDate)}
                          {daysRemaining !== null && (
                            <span
                              className={`ml-1 ${daysRemaining < 0 ? "text-red-600" : daysRemaining <= 3 ? "text-yellow-600" : "text-green-600"}`}
                            >
                              (
                              {daysRemaining < 0
                                ? "Overdue"
                                : `${daysRemaining} days left`}
                              )
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <select
                      value={task.status}
                      onChange={(e) =>
                        onUpdateTaskStatus(task.id, e.target.value)
                      }
                      className={`px-2 py-1 text-xs rounded border ${getStatusColor(task.status)}`}
                    >
                      <option value="Pending">Pending</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Completed">Completed</option>
                      <option value="Blocked">Blocked</option>
                    </select>
                    <Button variant="ghost" size="sm">
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDeleteTask(task.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Subtasks */}
                {isExpanded && task.subTasks && task.subTasks.length > 0 && (
                  <div className="mt-4 ml-8 space-y-2">
                    {task.subTasks.map((subTask) => {
                      const subAssignee = availableUsers.find(
                        (u) => u.id === subTask.assigneeId,
                      );
                      const subDaysRemaining = subTask.dueDate
                        ? getDaysRemaining(subTask.dueDate)
                        : null;

                      return (
                        <div
                          key={subTask.id}
                          className="border-l-2 border-muted pl-4 py-2"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <p className="text-sm font-medium">
                                {subTask.description}
                              </p>
                              <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1">
                                <span>
                                  Assigned to: {subAssignee?.name || "Unassigned"}
                                </span>
                                {subTask.dueDate && (
                                  <span className="flex items-center gap-1">
                                    <CalendarDays className="h-3 w-3" />
                                    Due: {formatDate(subTask.dueDate)}
                                    {subDaysRemaining !== null && (
                                      <span
                                        className={`ml-1 ${subDaysRemaining < 0 ? "text-red-600" : subDaysRemaining <= 3 ? "text-yellow-600" : "text-green-600"}`}
                                      >
                                        (
                                        {subDaysRemaining < 0
                                          ? "Overdue"
                                          : `${subDaysRemaining} days left`}
                                        )
                                      </span>
                                    )}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge
                                className={getStatusColor(subTask.status)}
                                variant="outline"
                              >
                                {subTask.status}
                              </Badge>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => onDeleteTask(subTask.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50 h-6 w-6 p-0"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
